import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/store';
import {
  selectAssessmentsAccessTokenExpiresAt,
  setAssessmentsAccessToken,
  updateAssessmentsAccessToken,
} from '../assessmentsAuthSlice';
import { AssessmentsNativeOutboundEventType } from '../constants';
import { isInNativeAssessmentsWebview, notifyNativeApp } from '../utils/nativeBridge';
import { getRefreshDelayMs } from '../utils/assessmentsTokenUtils';

// Keeps the access token fresh inside the mobile app's webview. The web owns the timer so the
// iOS and Android apps don't each implement one: shortly before the token's expiresAt (delivered
// by SET_AUTH, then by each UPDATE_TOKEN) it sends REFRESH_TOKEN, native replies immediately with
// {status: 'waiting'} and delivers the refreshed token later as an UPDATE_TOKEN inbound event.
// That event carries the new expiresAt, which schedules the next REFRESH_TOKEN. Without an
// expiresAt nothing is scheduled. Outside the webview this does nothing.
//
// Returns the handler for UPDATE_TOKEN. Events arrive through the shared inbound queue, which has
// a single consumer (see useAssessmentsNativeBridge) - so that consumer calls this handler rather
// than this hook draining the queue itself, which would swallow events meant for other handlers.
export const useAssessmentsTokenRefresh = () => {
  const dispatch = useAppDispatch();
  const expiresAt = useSelector(selectAssessmentsAccessTokenExpiresAt);

  useEffect(() => {
    if (!isInNativeAssessmentsWebview() || !expiresAt) {
      return undefined;
    }

    const delayMs = getRefreshDelayMs(expiresAt);
    if (delayMs == null) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      notifyNativeApp({ type: AssessmentsNativeOutboundEventType.REFRESH_TOKEN });
    }, delayMs);

    return () => clearTimeout(timerId);
  }, [expiresAt]);

  return useCallback(
    (accessToken: string, newExpiresAt?: string) => {
      dispatch(
        newExpiresAt
          ? setAssessmentsAccessToken({ accessToken, expiresAt: newExpiresAt })
          : updateAssessmentsAccessToken(accessToken),
      );
    },
    [dispatch],
  );
};
